import { Api } from '../api/root-api';
import {
  workspaceDetails,
  AccountDetailsRequest,
  AccountDetailsResponse,
  CustomerInvoicesRequest,
  CustomerInvoicesResponse,
  InvoiceBysellerRequest,
  ListInvoices,
  InvoiceList,
  InvoiceRequest,
  CommunicationResponse,
  CommunicationRequest,
  InvoiceCommunicationRequest,
  Invoice,
  InvoiceUpdateRequest,
  InvoiceIds,
  InvoiceIdsRequest,
  InvoiceFilter,
  FilteredInvoices,
  InvoiceAgingGraphDataResponse,
  GraphDataRequest,
  InvoiceReportsGraphDataResponse,
  InvoiceDetailsRequest,
  InvoiceParsedDataRequest,
  InvoiceParsedDataResponse,
  createDispatchNoteReq,
  DispatchListResponse,
  DispatchListRequest,
  createDispatchNoteResponse,
  DispatchListDetailsRes,
  DispatchNoteParsedDataRequest,
  DispatchNoteParsedDataResponse,
  ScheduleInvoiceReminderReq,
  ScheduleInvoiceReminderRes,
  DeleteInvoiceReminderReq,
  GetInvoiceRemindersReq,
  DeleteInvoiceReminderRes,
  GetTransactionTypeMapResponse,
  GetTransactionTypeMapRequest,
  WorkspaceTransactionTypeMapResponse,
  WorkspaceTransactionTypeMapRequest,
  DeleteTransactionTypeMapResponse,
  DeleteTransactionTypeMapRequest,
  ValidateTransactionTypeResponse,
  ValidateTransactionTypeRequest,
} from './model';
import {
  addAccountDetails,
  getOutstandingCustomerList,
} from '../customers/slice';
import {
  ListFilterKeys,
  ListInputItemOptions,
  Mappings,
  MobileSiftScreen,
} from '../filter/model';
import {
  addListInputItems,
  updateMobileFiltersByScreen,
} from '../filter/slice';
import {
  createListFiltersData,
  getStatusFilters,
  mapDataToInputItems,
} from '../filter/helpers';
import { getConfigValue, getQueryParams, isWeb } from '@zonofi/common';

let INVITE_ID: string;

export const invoiceApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getInvoiceDetailsBySeller: builder.mutation<
      ListInvoices,
      InvoiceBysellerRequest
    >({
      query(InvoiceBysellerRequest: InvoiceBysellerRequest) {
        return {
          url: `invoiceservice/invoiceBySeller`,
          method: 'GET',
          params: InvoiceBysellerRequest,
        };
      },
    }),
    getWorkSpaceDetailsById: builder.query<workspaceDetails, string>({
      query: (workSpaceId) => `guest/workspace/${workSpaceId}`,
    }),
    getAccountDetails: builder.mutation<
      AccountDetailsResponse,
      AccountDetailsRequest
    >({
      query(AccountDetailsRequest: AccountDetailsRequest) {
        const { inviteId } = AccountDetailsRequest;
        INVITE_ID = inviteId;
        return {
          url: `invoiceservice/party/vaccount`,
          method: 'GET',
          params: AccountDetailsRequest,
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(addAccountDetails({ accountDetails: data, id: INVITE_ID }));
        } catch (err) {
          console.log(err, 'dispatchErr');
        }
      },
    }),
    getCustomerInvoices: builder.mutation<
      CustomerInvoicesResponse,
      CustomerInvoicesRequest
    >({
      query(CustomerInvoicesRequest: CustomerInvoicesRequest) {
        return {
          url: `invoiceservice/partyAccountBook/balanceBySeller`,
          method: 'GET',
          params: CustomerInvoicesRequest,
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(getOutstandingCustomerList(data));
        } catch (err) {
          console.log(err, 'dispatchErr');
        }
      },
    }),
    getAllInvoice: builder.mutation<InvoiceList, InvoiceRequest>({
      query(arg) {
        const { workspaceId, body, ...params } = arg;
        return {
          url: `invoiceservice/invoices/all/${workspaceId}`,
          method: 'POST',
          body: body || {},
          params: params,
        };
      },
    }),
    getInvoiceList: builder.query<InvoiceList, InvoiceRequest>({
      query(arg) {
        const { workspaceId, ...params } = arg;
        const queryParams = getQueryParams(params);
        return {
          url: `invoiceservice/invoices/${workspaceId}?${queryParams}`,
          method: 'GET',
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        if (request.dispatchFilter)
          dispatch(
            updateMobileFiltersByScreen({
              screen: MobileSiftScreen.INVOICES,
              filter: {},
              loading: true,
            })
          );
        queryFulfilled
          .then((response) => {
            const { data } = response;
            if (request?.dispatchFilter) {
              if (isWeb()) {
                const customerInputItems = response?.data?.customers?.map(
                  (x) => ({
                    id: x?.customerId,
                    value: x?.name,
                    selected: false,
                    eitherOr: true,
                  })
                );
                dispatch(
                  addListInputItems({
                    inputItems: customerInputItems,
                    key: ListInputItemOptions.cfaPrincipalCustomers,
                  })
                );
              } else {
                const mapping: Mappings<InvoiceList>[] = [
                  {
                    dataKey: 'customers',
                    idKey: 'customerId',
                    valueKey: 'name',
                    filterKey: ListFilterKeys.Customers,
                    multiSelect: false,
                  },
                ];
                const listFilter = createListFiltersData(data, mapping) || {};
                let invoiceStatues = data.summary.invoiceStatus;
                if (request.filterPending) {
                  invoiceStatues = invoiceStatues?.filter(
                    (status) => status !== 'PD'
                  );
                }

                const invoiceStatus = getStatusFilters(invoiceStatues);
                listFilter[ListFilterKeys.InvoiceStatus] = {
                  multiSelect: false,
                  value: invoiceStatus,
                };

                dispatch(
                  updateMobileFiltersByScreen({
                    screen: MobileSiftScreen.INVOICES,
                    loading: false,
                    filter: { listFilter },
                  })
                );
              }
            }
          })
          .finally(() => {
            if (request.dispatchFilter)
              dispatch(
                updateMobileFiltersByScreen({
                  screen: MobileSiftScreen.INVOICES,
                  filter: {},
                  loading: false,
                })
              );
          });
      },
    }),
    getCommunicationTemplate: builder.query<
      CommunicationResponse,
      CommunicationRequest
    >({
      query(arg) {
        return {
          url: `invoiceservice/communication/invoices/template`,
          method: 'GET',
          params: arg,
        };
      },
    }),
    triggerShareEvent: builder.mutation<
      {
        success: boolean;
        statusCode: number;
        responseStatus: string;
      },
      InvoiceCommunicationRequest
    >({
      query(arg) {
        return {
          url: `invoiceservice/communication/invoices`,
          method: 'POST',
          body: arg,
        };
      },
    }),
    getInvoiceDetails: builder.query<Invoice, InvoiceDetailsRequest>({
      query(arg) {
        const { inviteId, ...params } = arg;
        return {
          url: `invoiceservice/invoice/${inviteId}?includePayment=true`,
          method: 'GET',
          params: params,
        };
      },
    }),
    createInvoice: builder.mutation<Invoice, any>({
      query(arg) {
        const { workspaceId, ...body } = arg;
        return {
          url: `invoiceservice/invoice/${workspaceId}`,
          method: 'POST',
          body: body,
        };
      },
    }),
    updateInvoice: builder.mutation<Invoice, any>({
      query(arg) {
        const { workspaceId, ...body } = arg;
        return {
          url: `invoiceservice/invoice/${workspaceId}`,
          method: 'PUT',
          body: body,
        };
      },
    }),
    getAllIds: builder.mutation<InvoiceIds, InvoiceIdsRequest>({
      query(arg) {
        const { workspaceId, ...body } = arg;
        return {
          url: `invoiceservice/reports/invoices/allIds/${workspaceId}`,
          body: body,
          method: 'POST',
        };
      },
    }),
    filterInvoices: builder.mutation<FilteredInvoices, InvoiceFilter>({
      query(arg) {
        const { workspaceId, ...body } = arg;
        return {
          url: `invoiceservice/reports/invoices/${workspaceId}`,
          body: body,
          method: 'POST',
        };
      },
    }),
    downloadInvoiceDetails: builder.query<any, string>({
      query(arg) {
        return {
          url: `invoiceservice/invoice/download-invoice-details?invoiceId=${arg}`,
          method: 'GET',
          responseHandler(response) {
            return response.arrayBuffer();
          },
        };
      },
    }),
    getInvoiceAgingGraphData: builder.query<
      InvoiceAgingGraphDataResponse,
      GraphDataRequest
    >({
      query(params) {
        return {
          url: `${getConfigValue('API_ENDPOINT')}api/reports/get-invoice-aging-report`,
          method: 'GET',
          params: params,
        };
      },
    }),
    getInvoiceDailyReportsGraphData: builder.query<
      InvoiceReportsGraphDataResponse,
      GraphDataRequest
    >({
      query(params) {
        return {
          url: `${getConfigValue('API_ENDPOINT')}api/reports/get-daily-invoice-reports`,
          method: 'GET',
          params: params,
        };
      },
    }),
    getInvoiceParsedData: builder.query<
      InvoiceParsedDataResponse,
      InvoiceParsedDataRequest
    >({
      query(params) {
        const { body, ...otherParams } = params;
        return {
          url: `hub/bot/api/v1/messages`,
          method: 'POST',
          params: otherParams,
          body: body,
        };
      },
    }),
    getDispatchNoteParsedData: builder.query<
      DispatchNoteParsedDataResponse,
      DispatchNoteParsedDataRequest
    >({
      query(params) {
        return {
          url: `invoiceservice/dispatch-parser`,
          method: 'POST',
          params: { workspaceId: params.workspaceId },
          body: params.body,
        };
      },
    }),
    createDispatchNote: builder.mutation<
      createDispatchNoteResponse,
      createDispatchNoteReq
    >({
      query(args) {
        return {
          url: `/invoiceservice/dispatch/${args.workspaceId}`,
          method: 'POST',
          body: args,
        };
      },
    }),
    getDispatchNoteList: builder.query<
      DispatchListResponse,
      DispatchListRequest
    >({
      query(args) {
        return {
          url: `/invoiceservice/dispatch/${args.workspaceId}?${getQueryParams(args.body)}`,
          method: 'get',
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        if (request.dispatchFilter)
          dispatch(
            updateMobileFiltersByScreen({
              screen: MobileSiftScreen.DISPATCH,
              filter: {},
              loading: true,
            })
          );
        queryFulfilled
          .then((response) => {
            if (isWeb()) {
              const customerList = mapDataToInputItems(
                response.data.customers,
                'customerId',
                'name'
              )?.filter((x: any) => x.value);
              dispatch(
                addListInputItems({
                  inputItems: customerList,
                  key: ListInputItemOptions.customers,
                })
              );
            } else {
              if (request.dispatchFilter) {
                const mapping: Mappings<DispatchListResponse>[] = [
                  {
                    dataKey: 'customers',
                    idKey: 'customerId',
                    valueKey: 'name',
                    filterKey: ListFilterKeys.Customers,
                    multiSelect: false,
                  },
                ];
                const listFilter =
                  createListFiltersData(response.data, mapping) || {};
                dispatch(
                  updateMobileFiltersByScreen({
                    screen: MobileSiftScreen.DISPATCH,
                    loading: false,
                    filter: { listFilter },
                  })
                );
              }
            }
          })
          .finally(() => {
            if (request.dispatchFilter)
              dispatch(
                updateMobileFiltersByScreen({
                  screen: MobileSiftScreen.DISPATCH,
                  filter: {},
                  loading: false,
                })
              );
          });

        const statsArray = [
          { id: '0', name: 'Delivered' },
          { id: '1', name: 'Dispatched' },
        ];
        const statusList = mapDataToInputItems(statsArray, 'id', 'name').filter(
          (x: any) => x.value
        );

        dispatch(
          addListInputItems({
            inputItems: statusList,
            key: ListInputItemOptions.dispatchStatus,
          })
        );
      },
    }),
    getDispatchNoteDetails: builder.query<
      DispatchListDetailsRes,
      { dispatchId: string; workspaceId: string }
    >({
      query(args) {
        return {
          url: `/invoiceservice/dispatch/details/${args.dispatchId}/${args.workspaceId}`,
          method: 'get',
        };
      },
    }),
    scheduleInvoceReminder: builder.mutation<
      ScheduleInvoiceReminderRes,
      ScheduleInvoiceReminderReq
    >({
      query(args) {
        const { sellerWorkspaceId } = args?.params;
        return {
          url: `hub/reports/api/v2/invoice-reminder/scheduler/create/${sellerWorkspaceId}`,
          method: 'POST',
          body: args?.body,
          params: { sellerWorkspaceId },
        };
      },
    }),
    deleteInvoiceReminder: builder.query<
      DeleteInvoiceReminderRes,
      DeleteInvoiceReminderReq
    >({
      query(args) {
        const { sellerWorkspaceId } = args.params;
        return {
          url: `hub/reports/api/v2/invoice-reminder/scheduler/delete/${sellerWorkspaceId}/${args.body.paymentReminderId}`,
          method: 'DELETE',
          params: { sellerWorkspaceId },
        };
      },
    }),
    getInvoiceReminders: builder.query<any, GetInvoiceRemindersReq>({
      query(args) {
        const { sellerWorkspaceId } = args.params;
        return {
          url: `hub/reports/api/v2/invoice-reminder/scheduler/getAll/${sellerWorkspaceId}`,
          method: 'GET',
          params: { sellerWorkspaceId },
        };
      },
    }),
    updateWorkspaceTransactionTypeMap: builder.mutation<
      WorkspaceTransactionTypeMapResponse,
      WorkspaceTransactionTypeMapRequest
    >({
      query(args) {
        return {
          url: `/invoiceservice/workspace-transaction-type-map/${args.workspaceId}`,
          method: 'POST',
          body: args.body,
        };
      },
    }),
    getTransactionTypeMap: builder.query<
      GetTransactionTypeMapResponse,
      GetTransactionTypeMapRequest
    >({
      query(args) {
        return {
          url: `/invoiceservice/workspace-transaction-type-map/${args.workspaceId}`,
          method: 'GET',
        };
      },
    }),
    deleteTransactionTypeMap: builder.mutation<
      DeleteTransactionTypeMapResponse,
      DeleteTransactionTypeMapRequest
    >({
      query(args) {
        return {
          url: `/invoiceservice/workspace-transaction-type-map/${args.workspaceId}/${args.transactionTypeId}`,
          method: 'DELETE',
        };
      },
    }),
    validateTransactionTypeMap: builder.mutation<
      ValidateTransactionTypeResponse,
      ValidateTransactionTypeRequest
    >({
      query(args) {
        return {
          url: `/invoice-serivce/workspace-transaction-type-map/validate/${args.params.sellerWorkspaceId}`,
          method: 'POST',
          params: args.params,
          body: args.body,
        };
      },
    }),
  }),
});

export const {
  useGetInvoiceDetailsBySellerMutation,
  useLazyGetWorkSpaceDetailsByIdQuery,
  useGetAccountDetailsMutation,
  useGetCustomerInvoicesMutation,
  useLazyGetInvoiceListQuery,
  useLazyGetCommunicationTemplateQuery,
  useLazyGetInvoiceDetailsQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useTriggerShareEventMutation,
  useGetAllIdsMutation,
  useFilterInvoicesMutation,
  useLazyDownloadInvoiceDetailsQuery,
  useGetInvoiceAgingGraphDataQuery,
  useLazyGetInvoiceAgingGraphDataQuery,
  useLazyGetInvoiceDailyReportsGraphDataQuery,
  useGetInvoiceDailyReportsGraphDataQuery,
  useGetAllInvoiceMutation,
  useLazyGetInvoiceParsedDataQuery,
  useCreateDispatchNoteMutation,
  useLazyGetDispatchNoteListQuery,
  useLazyGetDispatchNoteDetailsQuery,
  useLazyGetDispatchNoteParsedDataQuery,
  useScheduleInvoceReminderMutation,
  useLazyGetInvoiceRemindersQuery,
  useLazyDeleteInvoiceReminderQuery,
  useUpdateWorkspaceTransactionTypeMapMutation,
  useLazyGetTransactionTypeMapQuery,
  useDeleteTransactionTypeMapMutation,
  useValidateTransactionTypeMapMutation,
} = invoiceApi;
